import type { HrZoneId } from './heartRateZones';
import { buildHrZones } from './heartRateZones';
import { formatElapsedSpeech } from './formatPace';
import { normalizeCoachPlanText } from './runningCoach';
import type { PersonalPaceProfile } from './personalPaceZones';
import { inferZoneFromPersonalPace, paceForPersonalZone } from './personalPaceZones';
import { parseRaceGoal, isSingleRaceGoalPlan, isPaceTargetPlan, isIntervalStylePlan, isRacePaceReferenceOnly, type RaceGoal } from './raceGoal';

/**
 * Parses a free-form running plan written by Arnold or the user into a
 * sequence of timed phases that the live voice coach can follow turn by turn.
 *
 * The parser is intentionally lenient: most plans are 3-5 lines of prose like
 *
 *   - "5 minute warmup at easy Z2 pace"
 *   - "6 x 800m at threshold, 90 sec recovery jog"
 *   - "10 min cooldown"
 *
 * We extract structured phases when we can, but always fall back to a single
 * "free run" phase so the coach can still give pacing cues even if the plan is
 * unparseable.
 */

export type RunPhaseKind =
  | 'warmup'
  | 'cooldown'
  | 'recovery'
  | 'interval'
  | 'tempo'
  | 'easy'
  | 'long'
  | 'free';

export interface RunPhase {
  kind: RunPhaseKind;
  /** Index in the global ordered timeline (0-based). */
  index: number;
  /** Display label, e.g. "Warm-up", "Interval 3 of 6". */
  label: string;
  /** Total duration of the phase in seconds, when known. */
  durationSeconds?: number;
  /** Distance target in km, when the phase is distance-bounded. */
  distanceKm?: number;
  /** Target heart-rate zone for this phase, when stated. */
  targetZone?: HrZoneId;
  /** Target pace seconds per kilometer (lower bound = fastest). */
  paceSecondsPerKm?: number;
  /** Goal finish time for distance races (10K in 40 min) — pacing only, not phase clock. */
  targetTimeSeconds?: number;
  /** Repetition number within a set (1-indexed) and total reps. */
  repInSet?: { rep: number; totalReps: number };
  /** Short cue spoken when the phase begins. */
  startCue: string;
  /** Short cue spoken near the end of the phase. */
  endCue?: string;
}

export interface ParsedRunStructure {
  phases: RunPhase[];
  /** Total planned duration of all phases that have a duration. */
  totalPlannedSeconds: number;
  /** True when the parser produced at least one structured (non-free) phase. */
  hasStructure: boolean;
}

const ZONE_REGEX = /\b(?:zone\s*([1-5])|z([1-5]))\b/i;
const DURATION_REGEX = /(\d+(?:\.\d+)?)\s*(min(?:ute)?s?|mins?|sec(?:ond)?s?|(?<![a-z])s(?!\w))/i;
// Bare "m" only when not starting "min" (e.g. "800 m", not "10 min").
// Negative lookbehind: do not match the fractional digit of decimals ("1.5 km" ≠ "5 km").
const DISTANCE_REGEX = /(?<![\d.,])(\d+(?:[.,]\d+)?)\s*(km|kilometers?|k\b|meters?|m(?!in(?:ute)?s?\b))/i;
const PACE_REGEX = /(\d{1,2}):(\d{2})\s*(?:\/\s*km|min\s*\/\s*km|pace)?/i;
const MIN_PER_KM_REGEX = /(\d+(?:[.,]\d+)?)\s*min\s*(?:\/|per)\s*k(?:m|ilometer)?/i;
const REPS_REGEX =
  /(\d+)\s*(?:[x×]|times?)\s*(\d+(?:[.,]\d+)?)\s*(km|kilometers?|k\b|m|meters?|min(?:utes?)?|sec(?:onds?)?|s\b)?/i;

const RECOVERY_DURATION =
  /(\d+(?:[.,]\d+)?)\s*(min(?:ute)?s?|mins?|sec(?:ond)?s?|s)\b/i;

/** Natural speech for heart-rate zones — "Zone 4", never "Z4". */
export function zoneSpeechLabel(zone?: HrZoneId): string {
  if (!zone) return '';
  const n = zone.replace(/^z/i, '');
  return `Zone ${n}`;
}

function paceFromDistanceAndDuration(
  distanceKm: number,
  durationSeconds: number,
): number | undefined {
  if (!distanceKm || !durationSeconds || distanceKm <= 0 || durationSeconds <= 0) {
    return undefined;
  }
  return durationSeconds / distanceKm;
}

function recoveryDurationToSeconds(valRaw: string, unitRaw: string): number | undefined {
  const val = parseFloat(valRaw.replace(',', '.'));
  if (!Number.isFinite(val) || val <= 0) return undefined;
  const unit = unitRaw.toLowerCase();
  if (unit.startsWith('min') || unit === 'mins') return Math.round(val * 60);
  return Math.round(val);
}

/** Parse recovery duration from interval lines, e.g. "90s jog recovery", "with 1.5 min easy recovery". */
function parseRecoverySeconds(text: string): number | undefined {
  const patterns: RegExp[] = [
    /(?:,\s*|with\s+|between\s+)(\d+(?:[.,]\d+)?)\s*(?:s|sec(?:onds?)?)\s*(?:(?:easy|slow)\s+)?(?:jog|walk|recovery|rest)/i,
    /(?:,\s*|with\s+)(\d+(?:[.,]\d+)?)\s*min(?:ute)?s?\s*(?:(?:easy|slow)\s+)?(?:jog|recovery|rest)/i,
    /(\d+(?:[.,]\d+)?)\s*(?:s|sec(?:onds?)?)\s*(?:(?:easy|slow)\s+)?(?:jog|walk)\s*recovery/i,
    /(\d+(?:[.,]\d+)?)\s*(?:s|sec(?:onds?)?)\s*(?:recovery|rest)\s*(?:between|jog)?/i,
    /recovery\s*(?:of\s+)?(\d+(?:[.,]\d+)?)\s*(min(?:ute)?s?|mins?|sec(?:ond)?s?|s)\b/i,
    /(\d+(?:[.,]\d+)?)\s*(?:s|sec(?:onds?)?)\s*(?:between\s+)?(?:reps?|intervals?|efforts?)/i,
    /(?:with|between|,)\s*(\d+(?:[.,]\d+)?)\s*(min(?:ute)?s?|mins?|sec(?:ond)?s?|s)\b[^.\n]*(?:recovery|rest|jog|walk)/i,
    /(\d+(?:[.,]\d+)?)\s*(min(?:ute)?s?|mins?|sec(?:ond)?s?|s)\b\s*(?:easy|slow)?\s*(?:jog|walk)?\s*(?:recovery|rest)/i,
    /(?:with\s+|,\s*)?(\d+)\s*(?:rest|recovery)(?:\s+in\s+between)?/i,
    /(?:with\s+|,\s*)?(\d+)\s*(?:s|sec(?:onds?)?)\s*(?:rest|recovery)(?:\s+in\s+between)?/i,
    /^(\d+)\s+(?:easy|slow)\s+(?:jog|walk|recovery)\b/i,
  ];
  for (const re of patterns) {
    const m = text.match(re);
    if (!m) continue;
    const dur = m[0].match(RECOVERY_DURATION);
    if (dur) {
      const sec = recoveryDurationToSeconds(dur[1], dur[2]);
      if (sec && sec > 0) return sec;
    }
    const v = parseFloat(String(m[1]).replace(',', '.'));
    if (!Number.isFinite(v) || v <= 0) continue;
    const unit = m[0].toLowerCase();
    if (/\d+(?:[.,]\d+)?\s*min/.test(unit)) return Math.round(v * 60);
    return Math.round(v);
  }
  return undefined;
}

function buildRecoveryPhase(index: number, recoverySec: number): RunPhase {
  const recCue = cueFor('recovery', { durationSec: recoverySec });
  return {
    kind: 'recovery',
    index,
    label: 'Recovery',
    durationSeconds: recoverySec,
    startCue: recCue.start,
    endCue: recCue.end,
  };
}

interface GoalDistanceTime {
  distanceKm: number;
  durationSeconds: number;
  paceSecondsPerKm: number;
}

function parseGoalDistanceAndTime(text: string): GoalDistanceTime | null {
  const race = parseRaceGoal(text);
  if (!race) return null;
  return {
    distanceKm: race.distanceKm,
    durationSeconds: race.durationSeconds,
    paceSecondsPerKm: race.paceSecondsPerKm,
  };
}

function raceGoalToPhase(race: RaceGoal, index: number): RunPhase {
  const cue = cueFor('tempo', {
    pace: race.paceSecondsPerKm,
    distanceKm: race.distanceKm,
    durationSec: race.durationSeconds,
  });
  const label = /race$/i.test(race.label) ? race.label : `${race.label} race`;
  return {
    kind: 'tempo',
    index,
    label,
    distanceKm: race.distanceKm,
    targetTimeSeconds: race.durationSeconds,
    paceSecondsPerKm: race.paceSecondsPerKm,
    startCue:
      `Target ${race.label} in ${formatDurationSpeech(race.durationSeconds)} — that's about ${formatPaceHint(race.paceSecondsPerKm) ?? 'your goal pace'} per kilometer. I'll track your pace and keep you on schedule.`,
    endCue: cue.end ?? `Final stretch of your ${race.label} — give what you have left.`,
  };
}

/** e.g. "1km 5min pace" → 1 km at 5:00/km (not a timed race). */
function parseDistanceWithPaceTarget(text: string): RunPhase | null {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (!isPaceTargetPlan(normalized)) return null;

  const colonPace = normalized.match(
    /(?<![\d.,])(\d+(?:[.,]\d+)?)\s*(?:k(?:m)?|kilometers?)\s+(?:at\s+)?(\d{1,2}):(\d{2})\s*(?:\/\s*km|per\s*km|pace)?/i,
  );
  const minPace = normalized.match(
    /(?<![\d.,])(\d+(?:[.,]\d+)?)\s*(?:k(?:m)?|kilometers?)\s+(?:at\s+)?(\d+(?:[.,]\d+)?)\s*min(?:ute)?s?\s*pace\b/i,
  );

  let distanceKm: number | undefined;
  let paceSecondsPerKm: number | undefined;

  if (colonPace) {
    distanceKm = parseFloat(colonPace[1].replace(',', '.'));
    paceSecondsPerKm = parseInt(colonPace[2], 10) * 60 + parseInt(colonPace[3], 10);
  } else if (minPace) {
    distanceKm = parseFloat(minPace[1].replace(',', '.'));
    paceSecondsPerKm = Math.round(parseFloat(minPace[2].replace(',', '.')) * 60);
  }

  if (!distanceKm || !paceSecondsPerKm || distanceKm <= 0 || paceSecondsPerKm <= 0) return null;

  const distLabel =
    distanceKm < 1
      ? `${Math.round(distanceKm * 1000)} meters`
      : `${distanceKm.toFixed(distanceKm < 10 ? 1 : 0).replace(/\.0$/, '')} km`;
  const paceHint = formatPaceHint(paceSecondsPerKm) ?? 'your target pace';
  const cue = cueFor('tempo', { pace: paceSecondsPerKm, distanceKm });

  return {
    kind: 'tempo',
    index: 0,
    label: `${distLabel} at ${paceHint}`,
    distanceKm,
    paceSecondsPerKm,
    startCue: `Run ${distLabel} at about ${paceHint} per kilometer. I'll let you know when you're done.`,
    endCue: cue.end ?? 'Workout complete — great effort.',
  };
}

const parseDurationToSeconds = (raw: string): number | undefined => {
  // Pace tokens are not phase duration ("3:50 min/km", "3:45-3:55/km", "@ 4:30/km").
  if (/\d{1,2}:\d{2}\s*(?:-|–|to)\s*\d{1,2}:\d{2}/.test(raw)) return undefined;
  if (/\d{1,2}:\d{2}\s*min(?:ute)?s?\s*(?:\/|per)\s*k/i.test(raw)) return undefined;
  if (/(?:@|at)\s*\d{1,2}:\d{2}\s*(?:\/|per)\s*k/i.test(raw)) return undefined;
  if (/\d{1,2}:\d{2}\s*(?:\/\s*km|\/km|per\s*km)\b/i.test(raw)) return undefined;
  const m = raw.match(DURATION_REGEX);
  if (!m) return undefined;
  const val = parseFloat(m[1]);
  const unit = m[2].toLowerCase();
  if (!Number.isFinite(val)) return undefined;
  if (unit.startsWith('sec') || unit === 's') return Math.round(val);
  return Math.round(val * 60);
};

const parseDistanceToKm = (raw: string): number | undefined => {
  // Prefer full decimals first so "1.5 km" is never misread as "5 km".
  const decimal = raw.match(/(\d+[.,]\d+)\s*(km|kilometers?|k\b|meters?|m(?!in(?:ute)?s?\b))/i);
  if (decimal) {
    const val = parseFloat(decimal[1].replace(',', '.'));
    if (!Number.isFinite(val)) return undefined;
    const unit = decimal[2].toLowerCase();
    if (unit.startsWith('km') || unit === 'k' || unit.startsWith('kilom')) return val;
    return val / 1000;
  }
  const m = raw.match(DISTANCE_REGEX);
  if (!m) return undefined;
  const val = parseFloat(m[1].replace(',', '.'));
  if (!Number.isFinite(val)) return undefined;
  const unit = m[2].toLowerCase();
  if (unit.startsWith('km') || unit === 'k' || unit.startsWith('kilom')) return val;
  // assume meters
  return val / 1000;
};

const parsePaceToSec = (raw: string): number | undefined => {
  const paceRange = raw.match(/(\d{1,2}):(\d{2})\s*(?:-|–|to)\s*(\d{1,2}):(\d{2})/);
  if (paceRange) {
    const a = parseInt(paceRange[1], 10) * 60 + parseInt(paceRange[2], 10);
    const b = parseInt(paceRange[3], 10) * 60 + parseInt(paceRange[4], 10);
    if (a > 0 && b > 0) return Math.round((a + b) / 2);
  }
  const colonMinKm = raw.match(/(\d{1,2}):(\d{2})\s*min\s*(?:\/|per)\s*k/i);
  if (colonMinKm) {
    return parseInt(colonMinKm[1], 10) * 60 + parseInt(colonMinKm[2], 10);
  }
  const colon = raw.match(PACE_REGEX);
  if (colon) return parseInt(colon[1], 10) * 60 + parseInt(colon[2], 10);
  const paceLead = raw.match(/(\d{1,2}):(\d{2})\s*pace\b/i);
  if (paceLead) return parseInt(paceLead[1], 10) * 60 + parseInt(paceLead[2], 10);
  const minKm = raw.match(MIN_PER_KM_REGEX);
  if (minKm) {
    const val = parseFloat(minKm[1].replace(",", "."));
    if (Number.isFinite(val) && val > 0) return Math.round(val * 60);
  }
  return undefined;
};

/** Target pace for the active phase (seconds per km), when stated on that phase line. */
export function resolveTargetPaceForPhase(
  phase: RunPhase | null | undefined,
  _planText?: string,
): number | undefined {
  if (phase?.paceSecondsPerKm && phase.paceSecondsPerKm > 0) {
    return phase.paceSecondsPerKm;
  }
  return undefined;
};

const parseZone = (raw: string): HrZoneId | undefined => {
  const m = raw.match(ZONE_REGEX);
  if (!m) return undefined;
  const num = parseInt(m[1] || m[2], 10);
  if (!Number.isFinite(num) || num < 1 || num > 5) return undefined;
  return `z${num}` as HrZoneId;
};

/** Lines that are plan metadata, not runnable phases. */
const isMetadataLine = (line: string): boolean => {
  const l = line.toLowerCase().trim();
  if (/^estimated\s+duration\b/.test(l)) return true;
  if (/^pacing\s+guide\s*:?\s*$/i.test(l)) return true;
  if (/^total\s+distance\b/.test(l)) return true;
  if (/^main\s+set\s*:?\s*$/i.test(l)) return true;
  if (/^workout\s+type\b/.test(l)) return true;
  if (/^notes?\s*:?\s*$/.test(l)) return true;
  // Workout titles like "Tempo Control Mastery" — no duration/distance/reps.
  if (
    line.length < 64 &&
    !DURATION_REGEX.test(l) &&
    !DISTANCE_REGEX.test(l) &&
    !ZONE_REGEX.test(l) &&
    !REPS_REGEX.test(l) &&
    !PACE_REGEX.test(l) &&
    !/\d+\s*[x×]/.test(l)
  ) {
    if (/(?:run|jog|session|workout|mastery|builder|control|challenge|progression)$/i.test(l)) {
      return true;
    }
    // Title-only line with workout keywords but no actionable numbers.
    if (
      /(?:tempo|threshold|interval|recovery|aerobic|endurance|speed|fartlek)/i.test(l) &&
      !/\d/.test(l)
    ) {
      return true;
    }
  }
  return false;
};

/** Pacing-guide bullets and coaching notes — not separate runnable phases. */
const isPacingNoteLine = (line: string): boolean => {
  const l = line.toLowerCase().trim();
  if (/^pacing\s+guide/.test(l)) return true;
  if (/^why\s+this\s+works/.test(l)) return true;
  if (/^next\s+session/.test(l)) return true;
  if (/^focus\s+on\b/.test(l)) return true;
  if (/^notes?\s*:?\s*$/.test(l)) return true;
  // Pacing-guide bullets: "Warm-up: Z2", "Tempo: 4:00/km · Z3" (no phase duration).
  if (/^(warm-?up|cool-?down|tempo|threshold|main\s+set|steady|easy)\s*:/i.test(line)) {
    const hasDuration = DURATION_REGEX.test(line);
    const hasDistance = DISTANCE_REGEX.test(line);
    if (!hasDuration && !hasDistance) return true;
  }
  // "Warm-up: Easy conversational" or "4km: Easy conversational (~5:30/km)" without duration/distance target
  if (/^(warm-?up|cool-?down|\d+\s*km)\s*:/i.test(line)) {
    const hasDuration = DURATION_REGEX.test(line);
    const hasDistance = DISTANCE_REGEX.test(line);
    if (!hasDuration && !hasDistance && /conversational|moderate effort|comfortably hard|very easy/i.test(l)) {
      return true;
    }
  }
  return false;
};

const classifyLine = (line: string, labelHint?: RunPhaseKind | null): RunPhaseKind => {
  if (labelHint) return labelHint;
  const l = line.toLowerCase();
  if (/warm\s*up|warm-up/.test(l)) return 'warmup';
  if (/cool\s*down|cool-down/.test(l)) return 'cooldown';
  if (/^main\s+set\s*:?\s*/.test(l) && /tempo|threshold|comfortably hard|hard effort|steady/i.test(l)) {
    return 'tempo';
  }
  // "Z1 Recovery" in zone labels is not a recovery phase — only standalone recovery cues.
  if (
    /(?:^|\s)(?:recovery\s+jog|easy\s+recovery|rest\s+between|\d+\s*(?:s|sec|min).*recovery)/.test(l) ||
    (/recovery|easy\s+jog|rest/.test(l) && !/recovery run|z\d\s*recovery|zone\s*\d.*recovery/i.test(l))
  ) {
    return 'recovery';
  }
  if (/tempo|threshold/.test(l)) return 'tempo';
  if (/\d{1,2}:\d{2}\s*pace\s+\d/i.test(l)) return 'tempo';
  if (/\d+(?:[.,]\d+)?\s*k(?:m)?\b.*(?:z[3-5]|zone\s*[3-5])/i.test(l) && /pace|:\d{2}/i.test(l)) {
    return 'tempo';
  }
  if (/\brace\s+(?:effort|pace)|near\s+\d+\s*k.*\brace\b/i.test(l)) return 'tempo';
  if (/long\s+run/.test(l)) return 'long';
  if (/interval|[x×]\s*\d|repeat|repetition|sprints?/.test(l)) return 'interval';
  if (/easy|z[12]|zone\s*[12]/i.test(l)) return 'easy';
  return 'free';
};

const labelFor = (kind: RunPhaseKind, idx: number, rep?: { rep: number; totalReps: number }): string => {
  if (kind === 'warmup') return 'Warm-up';
  if (kind === 'cooldown') return 'Cool-down';
  if (kind === 'recovery') return 'Recovery';
  if (kind === 'tempo') return 'Tempo';
  if (kind === 'long') return 'Long run';
  if (kind === 'easy') return 'Easy run';
  if (kind === 'interval' && rep) return `Interval ${rep.rep} of ${rep.totalReps}`;
  if (kind === 'interval') return `Interval ${idx + 1}`;
  return 'Main set';
};

const labelFromLine = (line: string, kind: RunPhaseKind, idx: number): string => {
  if (/\brace\s+effort\b/i.test(line)) return 'Race effort';
  if (/\brace\s+pace\b/i.test(line)) return 'Race pace';
  return labelFor(kind, idx);
};

const blockNameForCoach = (kind: RunPhaseKind): string => {
  if (kind === 'warmup') return 'warm-up block';
  if (kind === 'cooldown') return 'cool-down block';
  if (kind === 'recovery') return 'recovery jog';
  if (kind === 'tempo') return 'tempo threshold block';
  if (kind === 'interval') return 'interval rep';
  if (kind === 'easy') return 'easy aerobic block';
  if (kind === 'long') return 'long-run segment';
  return 'main run block';
};

const describePhaseBrief = (phase: RunPhase): string => {
  if (phase.kind === 'interval' && phase.repInSet) {
    return `interval ${phase.repInSet.rep} of ${phase.repInSet.totalReps}`;
  }
  return blockNameForCoach(phase.kind);
};

/** Phase immediately after the given index in the plan timeline. */
export function getPhaseAfter(structure: ParsedRunStructure, phaseIndex: number): RunPhase | null {
  const i = structure.phases.findIndex((p) => p.index === phaseIndex);
  if (i < 0 || i + 1 >= structure.phases.length) return null;
  return structure.phases[i + 1];
};

export interface ActivePhaseCoachContext {
  label: string;
  /** Full spoken sentence for live coach (e.g. warm-up block, next phase). */
  summary: string;
  nextPhaseLabel?: string;
  phaseKind: RunPhaseKind;
}

/** Rich phase context for live coach — avoids generic "run phase" wording. */
export function describeActivePhaseForCoach(
  active: ActivePhaseState,
  structure: ParsedRunStructure,
): ActivePhaseCoachContext {
  const { phase, remainingSeconds, distanceIntoPhaseKm } = active;
  const next = getPhaseAfter(structure, phase.index);
  const zonePart = phase.targetZone ? `, targeting ${zoneSpeechLabel(phase.targetZone)}` : '';
  const timePart =
    remainingSeconds != null && remainingSeconds > 0 && remainingSeconds <= 3600
      ? ` About ${formatDurationSpeech(remainingSeconds)} left in this block.`
      : '';
  const nextPart = next ? ` Next up: ${describePhaseBrief(next)}.` : '';
  const blockMeters = phase.distanceKm ? Math.round(phase.distanceKm * 1000) : null;
  const intoMeters =
    distanceIntoPhaseKm != null ? Math.round(distanceIntoPhaseKm * 1000) : null;
  const remainMeters =
    blockMeters != null && intoMeters != null ? Math.max(0, blockMeters - intoMeters) : null;
  const distPart =
    blockMeters != null && intoMeters != null
      ? ` ${formatDistanceSpeechKm(intoMeters)} into this ${formatDistanceSpeechKm(blockMeters)} run${
          remainMeters != null && remainMeters > 0
            ? `, ${formatDistanceSpeechKm(remainMeters)} left.`
            : '.'
        }`
      : '';
  const phaseDurationSec = phase.durationSeconds ?? 0;
  const elapsedIntoPhaseSec =
    remainingSeconds != null && phaseDurationSec > 0
      ? Math.max(0, phaseDurationSec - remainingSeconds)
      : null;
  const elapsedPart =
    elapsedIntoPhaseSec != null && elapsedIntoPhaseSec > 0 && phaseDurationSec > 0
      ? ` You are ${formatDurationSpeech(elapsedIntoPhaseSec)} into this ${formatDurationSpeech(phaseDurationSec)} block.`
      : '';

  let summary: string;
  if (phase.kind === 'interval' && phase.repInSet) {
    summary = `You are in interval ${phase.repInSet.rep} of ${phase.repInSet.totalReps}${zonePart}.${distPart}${elapsedPart}${timePart}${nextPart}`;
  } else if (phase.kind === 'warmup') {
    summary = `You are in your warm-up block — easy effort only, do not hit interval pace yet.${zonePart ? `${zonePart}.` : ''}${elapsedPart}${timePart}${nextPart}`;
  } else if (phase.kind === 'cooldown') {
    summary = `You are in your cool-down block. Ease off and let heart rate settle.${elapsedPart}${timePart}`;
  } else if (phase.kind === 'tempo') {
    const racePart =
      phase.targetTimeSeconds && phase.distanceKm
        ? ` Race target: ${phase.label.replace(/ race$/i, '')} in ${Math.round(phase.targetTimeSeconds / 60)} minutes at about ${formatPaceHint(phase.paceSecondsPerKm) ?? 'goal pace'} per kilometer.`
        : '';
    summary = `You are in a tempo threshold block${zonePart}. Hold steady, comfortably hard effort.${racePart}${distPart}${elapsedPart}${timePart}${nextPart}`;
  } else if (phase.kind === 'recovery') {
    summary = `You are in a recovery jog between efforts — keep it very easy, ignore interval pace targets.${elapsedPart}${timePart}${nextPart}`;
  } else {
    summary = `You are in your ${blockNameForCoach(phase.kind)}${zonePart}.${distPart}${elapsedPart}${timePart}${nextPart}`;
  }

  return {
    label: phase.label,
    summary: summary.replace(/\s+/g, ' ').trim(),
    nextPhaseLabel: next?.label,
    phaseKind: phase.kind,
  };
}

const cueFor = (
  kind: RunPhaseKind,
  options: { rep?: { rep: number; totalReps: number }; zone?: HrZoneId; pace?: number; durationSec?: number; distanceKm?: number },
): { start: string; end?: string } => {
  const zoneText = options.zone ? `, target ${zoneSpeechLabel(options.zone)}` : '';
  const paceText = options.pace
    ? `, around ${Math.floor(options.pace / 60)}:${String(options.pace % 60).padStart(2, '0')} per kilometer`
    : '';
  const durText = options.durationSec ? ` for ${formatDurationSpeech(options.durationSec)}` : '';
  const distText = options.distanceKm ? ` for ${options.distanceKm.toFixed(2).replace(/\.?0+$/, '')} kilometers` : '';

  if (kind === 'warmup') {
    return {
      start: `Starting warm-up${durText || distText}${zoneText}. Easy effort, get the legs moving.`,
      end: 'Warm-up nearly done. Get ready to lift the pace.',
    };
  }
  if (kind === 'cooldown') {
    return {
      start: `Beginning cool-down${durText || distText}. Slow, relaxed jog.`,
      end: 'Cool-down nearly complete. Great work today.',
    };
  }
  if (kind === 'recovery') {
    return {
      start: `Recovery${durText || distText}. Easy jog or walk, lower the heart rate.`,
      end: 'Recovery ending. Get ready for the next effort.',
    };
  }
  if (kind === 'tempo') {
    return {
      start: `Tempo block${durText || distText}${zoneText}${paceText}. Comfortably hard, steady breathing.`,
      end: 'Tempo block nearly done. Hold the pace through the end.',
    };
  }
  if (kind === 'interval') {
    const repPart = options.rep ? `Interval ${options.rep.rep} of ${options.rep.totalReps}` : 'Interval';
    return {
      start: `${repPart}${distText || durText}${zoneText}${paceText}. Drive the effort now.`,
      end: 'Almost there. Hold to the line.',
    };
  }
  if (kind === 'long') {
    return {
      start: `Long run${durText || distText}${zoneText}. Settle in to a sustainable rhythm.`,
    };
  }
  if (kind === 'easy') {
    return {
      start: `Easy run${durText || distText}${zoneText}. Conversational effort.`,
    };
  }
  return {
    start: `Continue running${durText || distText}${zoneText}${paceText}.`,
  };
};

const formatDurationSpeech = (sec: number): string => formatElapsedSpeech(sec);

/** Natural spoken distance — e.g. 1640m → "1.64 kilometers", 500m → "500 meters". */
export const formatDistanceSpeechKm = (meters: number): string => {
  const m = Math.max(0, Math.round(meters));
  if (m >= 1000) {
    const km = Math.round((m / 1000) * 100) / 100;
    const spoken = km.toString().replace(/\.?0+$/, '');
    const label = km === 1 ? '1 kilometer' : `${spoken} kilometers`;
    return label;
  }
  return `${m} meter${m !== 1 ? 's' : ''}`;
};

const parseRepDistanceKm = (repUnit: string, repValue: number): number | undefined => {
  if (repUnit === 'km' || repUnit === 'k' || repUnit.startsWith('kilom')) return repValue;
  if (repUnit === 'm' || repUnit.startsWith('meter')) return repValue / 1000;
  return undefined;
};

const expandIntervalSet = (line: string, indexStart: number): RunPhase[] | null => {
  const m = line.match(REPS_REGEX);
  if (!m) return null;
  const totalReps = parseInt(m[1], 10);
  if (!Number.isFinite(totalReps) || totalReps < 1 || totalReps > 30) return null;
  const repValue = parseFloat(m[2].replace(',', '.'));
  const repUnit = (m[3] || '').toLowerCase();

  const distanceKm = parseRepDistanceKm(repUnit, repValue);
  const durationSeconds =
    repUnit.startsWith('min') ? Math.round(repValue * 60) : repUnit === 's' || repUnit.startsWith('sec') ? Math.round(repValue) : undefined;

  const zone = parseZone(line);
  const pace = parsePaceToSec(line);
  const recoverySec = parseRecoverySeconds(line);

  const out: RunPhase[] = [];
  for (let i = 0; i < totalReps; i++) {
    const rep = { rep: i + 1, totalReps };
    const cue = cueFor('interval', { rep, zone, pace, durationSec: durationSeconds, distanceKm });
    out.push({
      kind: 'interval',
      index: indexStart + out.length,
      label: labelFor('interval', indexStart + out.length, rep),
      durationSeconds,
      distanceKm,
      targetZone: zone,
      paceSecondsPerKm: pace,
      repInSet: rep,
      startCue: cue.start,
      endCue: cue.end,
    });
    if (recoverySec && i < totalReps - 1) {
      out.push(buildRecoveryPhase(indexStart + out.length, recoverySec));
    }
  }
  return out;
};

const ZONE_DISTANCE_CHUNK =
  /(?<![\d.,])(\d+(?:[.,]\d+)?)\s*(?:k(?:m)?|kilometers?)\s*(?:at\s*)?(?:zone\s*|z)\s*([1-5])/gi;

const ZONE_TIME_CHUNK =
  /(\d+)\s*min(?:ute)?s?\s*(?:at\s*)?(?:zone\s*|z)\s*([1-5])/gi;

const PACE_IN_LINE = /(\d{1,2}:\d{2})\s*(?:\/\s*k(?:m)?|pace|min)/i;

function formatRaceTimeForCoach(totalSeconds: number): string {
  if (totalSeconds >= 3600) {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.round((totalSeconds % 3600) / 60);
    return m > 0 ? `${h} hour${h !== 1 ? 's' : ''} ${m} minutes` : `${h} hour${h !== 1 ? 's' : ''}`;
  }
  const m = Math.round(totalSeconds / 60);
  return `${m} minute${m !== 1 ? 's' : ''}`;
}

function formatPaceHint(secPerKm?: number): string | undefined {
  if (!secPerKm || !Number.isFinite(secPerKm) || secPerKm <= 0) return undefined;
  const totalSec = Math.max(0, Math.round(secPerKm));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}/km`;
}

function zoneForNumber(num: number, zones: import('./heartRateZones').HrZone[]): import('./heartRateZones').HrZone | undefined {
  const id = `z${num}` as HrZoneId;
  return zones.find((z) => z.id === id);
}

const ZONE_EFFORT_HINTS: Record<HrZoneId, string> = {
  z1: 'very easy recovery jog',
  z2: 'easy conversational pace',
  z3: 'steady tempo — watch pace and HR',
  z4: 'threshold — hold pace and HR together',
  z5: 'hard interval / VO2 effort',
};

const INTERVAL_SET_REGEX =
  /(\d+)\s*(?:[x×]|times?)\s*(\d+(?:[.,]\d+)?)\s*(km|kilometers?|k\b|m|meters?|min(?:ute)?s?|sec(?:ond)?s?|s\b)?(?:\s*(?:intervals?|reps?|repeats?))?/i;

/** Infer HR zone from target pace when the runner only specifies tempo. */
/** Infer zone from effort words when pace/zone not stated (e.g. "fast pace", "easy jog"). */
function inferZoneFromEffortWords(text: string): HrZoneId | undefined {
  const lower = text.toLowerCase();
  if (/\b(vo2|max|sprint|all\s*out|hard|fast)\b/.test(lower)) return 'z5';
  if (/\b(threshold|tempo|comfortably\s*hard|race\s*pace)\b/.test(lower)) return 'z4';
  if (/\b(steady|moderate|marathon)\b/.test(lower)) return 'z3';
  if (/\b(easy|conversational|aerobic|jog)\b/.test(lower)) return 'z2';
  if (/\b(recovery|walk|very\s*easy)\b/.test(lower)) return 'z1';
  return undefined;
}

function inferZoneFromPace(paceSec: number): HrZoneId {
  if (paceSec <= 210) return 'z5';
  if (paceSec <= 255) return 'z4';
  if (paceSec <= 300) return 'z3';
  if (paceSec <= 360) return 'z2';
  return 'z1';
}

function resolveZoneForPace(
  paceSec: number,
  personalPaces?: PersonalPaceProfile | null,
): HrZoneId {
  if (personalPaces) return inferZoneFromPersonalPace(paceSec, personalPaces);
  return inferZoneFromPace(paceSec);
}

function resolvePaceForZone(
  zone: HrZoneId,
  personalPaces?: PersonalPaceProfile | null,
): number | undefined {
  if (!personalPaces) return undefined;
  return paceForPersonalZone(zone, personalPaces);
}

/** Natural coach line — no leading numbers (TTS reads "1." as "one"). */
function formatCoachPhaseLine(
  label: string,
  target: string,
  zones: import('./heartRateZones').HrZone[],
  opts?: {
    zNum?: number;
    zone?: import('./heartRateZones').HrZone;
    paceSec?: number;
    effortHint?: string;
  },
): string {
  const zNum =
    opts?.zNum ??
    (opts?.zone ? parseInt(opts.zone.id.replace('z', ''), 10) : undefined);
  const zone = opts?.zone ?? (zNum ? zoneForNumber(zNum, zones) : undefined);
  const pacePart = opts?.paceSec ? ` @ ${formatPaceHint(opts.paceSec)}` : '';
  const id = zone?.id as HrZoneId | undefined;
  const effort = opts?.effortHint ?? (id ? ZONE_EFFORT_HINTS[id] : undefined);
  if (zone && zNum) {
    const hr = `Zone ${zNum} ${zone.label}, ${zone.lowerBpm}–${zone.upperBpm} bpm`;
    const suffix = [pacePart, effort ? `— ${effort}` : ''].filter(Boolean).join(' ');
    return suffix
      ? `${label} — ${target} (${hr}${pacePart}${effort ? ` — ${effort}` : ''})`
      : `${label} — ${target} (${hr})`;
  }
  if (pacePart || effort) {
    const bits = [`target${pacePart}`, effort].filter(Boolean).join(' — ');
    return `${label} — ${target} (${bits})`;
  }
  return `${label} — ${target}`;
}

function parseIntervalSegment(
  text: string,
  personalPaces?: PersonalPaceProfile | null,
): {
  reps: number;
  value: number;
  unit: string;
  paceSec?: number;
  zone?: HrZoneId;
  recoverySec?: number;
} | null {
  const m = text.match(INTERVAL_SET_REGEX);
  if (!m) return null;
  const reps = parseInt(m[1], 10);
  const value = parseFloat(m[2].replace(',', '.'));
  if (!Number.isFinite(reps) || reps < 1 || reps > 40 || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  const unit = (m[3] || 'km').toLowerCase();
  const paceSec = parsePaceToSec(text);
  const zone =
    parseZone(text) ??
    (paceSec ? resolveZoneForPace(paceSec, personalPaces) : undefined) ??
    inferZoneFromEffortWords(text);
  const distanceKm = unit.startsWith('km') || unit === 'k' ? value : unit.startsWith('m') ? value / 1000 : undefined;
  const durationSeconds = unit.startsWith('min') ? Math.round(value * 60) : unit === 's' || unit.startsWith('sec') ? Math.round(value) : undefined;
  const recoverySec = parseRecoverySeconds(text);
  return { reps, value, unit, paceSec, zone, recoverySec };
}

function formatIntervalSegment(
  parsed: NonNullable<ReturnType<typeof parseIntervalSegment>>,
  zones: import('./heartRateZones').HrZone[],
  personalPaces?: PersonalPaceProfile | null,
): string {
  const unit = parsed.unit;
  let repTarget = '';
  if (unit.startsWith('km') || unit === 'k') {
    const kmLabel = parsed.value === 1 ? '1 km' : `${parsed.value} km`;
    repTarget = `${parsed.reps} × ${kmLabel}`;
  } else if (unit.startsWith('min')) {
    repTarget = `${parsed.reps} × ${parsed.value} minute${parsed.value !== 1 ? 's' : ''}`;
  } else if (unit.startsWith('sec') || unit === 's') {
    repTarget = `${parsed.reps} × ${parsed.value} second${parsed.value !== 1 ? 's' : ''}`;
  } else if (unit.startsWith('m')) {
    repTarget = `${parsed.reps} × ${parsed.value} m`;
  } else {
    repTarget = `${parsed.reps} × ${parsed.value} ${unit}`;
  }

  let target = `${repTarget} intervals`;
  if (parsed.recoverySec) {
    target += ` with ${formatDurationSpeech(parsed.recoverySec)} easy recovery between`;
  }

  const zNum = parsed.zone
    ? parseInt(parsed.zone.replace('z', ''), 10)
    : parsed.paceSec
      ? parseInt(resolveZoneForPace(parsed.paceSec, personalPaces).replace('z', ''), 10)
      : 4;
  const paceSec =
    parsed.paceSec ??
    (parsed.zone ? resolvePaceForZone(parsed.zone, personalPaces) : resolvePaceForZone(`z${zNum}` as HrZoneId, personalPaces));
  return formatCoachPhaseLine('Main set', target, zones, {
    zNum,
    zone: zoneForNumber(zNum, zones),
    paceSec,
  });
}

function parseWarmCoolPart(part: string): {
  kind: 'warmup' | 'cooldown';
  mins: number;
  zone: HrZoneId;
} | null {
  const lower = part.toLowerCase();
  const isWarm = /warm\s*up|warm-up|\bwarm\b/.test(lower);
  const isCool = /cool\s*down|cool-down|\bcool\b/.test(lower);
  if (!isWarm && !isCool) return null;
  const dur = part.match(/(\d+(?:\.\d+)?)\s*min(?:ute)?s?/i);
  const zone = parseZone(part) ?? ('z1' as HrZoneId);
  return {
    kind: isWarm ? 'warmup' : 'cooldown',
    mins: dur ? parseFloat(dur[1]) : 5,
    zone,
  };
}

function splitPlanSegments(text: string): string[] {
  if (text.includes('\n')) {
    return text.split(/[\n\r]+/).map((s) => s.trim()).filter(Boolean);
  }
  const trimmed = text.trim();
  // "6x800m @ 3:45, 90s jog recovery" — keep recovery on the same segment.
  if (
    INTERVAL_SET_REGEX.test(trimmed) &&
    /,\s*\d+(?:\.\d+)?\s*(?:s|sec(?:onds?)?|min(?:ute)?s?)\s*(?:jog|walk|recovery|rest)/i.test(trimmed)
  ) {
    return [trimmed];
  }
  return trimmed
    .split(/\s*,\s*(?=\d|\bwarm|\bcool|\d+\s*[x×])/i)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Strip leading list numbers — TTS reads "1." as "one". */
export function sanitizeCoachPlanLines(plan: string): string {
  return plan
    .split(/[\n\r]+/)
    .map((line) => line.replace(/^\s*\d+[.)]\s+/, '').trim())
    .filter(Boolean)
    .join('\n');
}

function segmentHasWarmCool(segments: string[], kind: 'warmup' | 'cooldown'): boolean {
  return segments.some((s) => parseWarmCoolPart(s)?.kind === kind);
}

/** Add easy Z1 warm-up / cool-down when the runner only typed a main set. */
function maybeWrapWithWarmCool(
  lines: string[],
  segments: string[],
  zones: import('./heartRateZones').HrZone[],
): string[] {
  if (lines.length === 0) return lines;

  const hasWarm = segmentHasWarmCool(segments, 'warmup');
  const hasCool = segmentHasWarmCool(segments, 'cooldown');
  if (hasWarm && hasCool) return lines;

  const mainOnlyLine =
    lines.length === 1 &&
    /^(Main set|Steady run|Steady block|Target run)\s*—/i.test(lines[0]);
  const mainOnlyInput =
    segments.length === 1 &&
    !parseWarmCoolPart(segments[0]) &&
    (INTERVAL_SET_REGEX.test(segments[0]) ||
      /tempo|threshold|interval|[x×]\s*\d/i.test(segments[0]));

  if (!mainOnlyLine && !mainOnlyInput) return lines;

  const result = [...lines];
  const z1 = zoneForNumber(1, zones);
  if (!hasWarm) {
    result.unshift(
      formatCoachPhaseLine('Warm-up', '5 minutes easy jog', zones, {
        zNum: 1,
        zone: z1,
      }),
    );
  }
  if (!hasCool) {
    result.push(
      formatCoachPhaseLine('Cool-down', '5 minutes easy jog', zones, {
        zNum: 1,
        zone: z1,
      }),
    );
  }
  return result;
}

export type StructureRunGuideOptions = {
  ageYears?: number | null;
  maxHr?: number | null;
  personalPaces?: PersonalPaceProfile | null;
};

/** Turn shorthand into numbered coach-ready phases with HR ranges and effort cues. */
export function structureRunGuideFromInput(
  raw: string,
  opts?: StructureRunGuideOptions,
): string {
  try {
    return structureRunGuideFromInputInner(raw, opts);
  } catch (err) {
    console.warn('[structureRunGuideFromInput] failed:', err instanceof Error ? err.message : String(err));
    return normalizeCoachPlanText(raw).trim();
  }
}

function structureRunGuideFromInputInner(
  raw: string,
  opts?: StructureRunGuideOptions,
): string {
  const normalized = normalizeCoachPlanText(raw).trim().replace(/^i\s+did\s+/i, '');
  const text = expandCommaSeparatedZoneSegments(normalized);
  if (!text) return '';

  const { zones } = buildHrZones({ ageYears: opts?.ageYears, maxHr: opts?.maxHr });
  const personalPaces = opts?.personalPaces ?? null;
  const lines: string[] = [];
  const segments = splitPlanSegments(text);
  const finish = () => maybeWrapWithWarmCool(lines, segments, zones).join('\n');

  for (const part of segments) {
    const warmCool = parseWarmCoolPart(part);
    if (warmCool) {
      const zNum = parseInt(warmCool.zone.replace('z', ''), 10);
      const mins = warmCool.mins;
      const label = warmCool.kind === 'warmup' ? 'Warm-up' : 'Cool-down';
      const target = `${mins} minute${mins !== 1 ? 's' : ''} easy jog`;
      lines.push(
        formatCoachPhaseLine(label, target, zones, {
          zNum,
          zone: zoneForNumber(zNum, zones),
        }),
      );
      continue;
    }

    if (INTERVAL_SET_REGEX.test(part)) {
      const interval = parseIntervalSegment(part, personalPaces);
      if (interval) {
        lines.push(formatIntervalSegment(interval, zones, personalPaces));
        continue;
      }
    }

    const zoneDist = part.match(
      /(?<![\d.,])(\d+(?:[.,]\d+)?)\s*(?:k(?:m)?|kilometers?)\s*(?:at\s*)?(?:@?\s*)?(?:(\d{1,2}:\d{2})\s*(?:\/\s*k(?:m)?|pace)?\s*)?(?:in\s*)?(?:zone\s*|z)\s*([1-5])/i,
    );
    if (zoneDist) {
      const km = zoneDist[1].replace(',', '.');
      const zNum = parseInt(zoneDist[3], 10);
      const zId = `z${zNum}` as HrZoneId;
      const paceSec =
        zoneDist[2] ? parsePaceToSec(zoneDist[2]) : resolvePaceForZone(zId, personalPaces);
      lines.push(
        formatCoachPhaseLine(
          'Steady run',
          `Run ${km} km`,
          zones,
          { zNum, zone: zoneForNumber(zNum, zones), paceSec },
        ),
      );
      continue;
    }

    const zoneTime = part.match(
      /(\d+(?:\.\d+)?)\s*min(?:ute)?s?\s*(?:at\s*)?(?:@?\s*)?(?:(\d{1,2}:\d{2})\s*(?:\/\s*k(?:m)?|pace)?\s*)?(?:in\s*)?(?:zone\s*|z)\s*([1-5])/i,
    );
    if (zoneTime) {
      const mins = parseFloat(zoneTime[1]);
      const zNum = parseInt(zoneTime[3], 10);
      const zId = `z${zNum}` as HrZoneId;
      const paceSec =
        zoneTime[2] ? parsePaceToSec(zoneTime[2]) : resolvePaceForZone(zId, personalPaces);
      lines.push(
        formatCoachPhaseLine(
          'Steady block',
          `${mins} minute${mins !== 1 ? 's' : ''}`,
          zones,
          { zNum, zone: zoneForNumber(zNum, zones), paceSec },
        ),
      );
      continue;
    }

    const dur = part.match(/(\d+(?:\.\d+)?)\s*min(?:ute)?s?/i);
    const zoneM = part.match(/(?:zone\s*|z)\s*([1-5])/i);
    const paceM = part.match(PACE_IN_LINE);
    if (dur || zoneM || paceM) {
      const paceSec = paceM ? parsePaceToSec(part) : undefined;
      const zNum = zoneM
        ? parseInt(zoneM[1], 10)
        : paceSec
          ? parseInt(resolveZoneForPace(paceSec, personalPaces).replace('z', ''), 10)
          : undefined;
      const zId = zNum ? (`z${zNum}` as HrZoneId) : undefined;
      const resolvedPace =
        paceSec ?? (zId ? resolvePaceForZone(zId, personalPaces) : undefined);
      const target = dur
        ? `${parseFloat(dur[1])} minute${parseFloat(dur[1]) !== 1 ? 's' : ''}`
        : part.slice(0, 48);
      const label = /tempo|threshold/.test(part.toLowerCase()) ? 'Tempo block' : 'Steady block';
      lines.push(
        formatCoachPhaseLine(label, target, zones, {
          zNum,
          zone: zNum ? zoneForNumber(zNum, zones) : undefined,
          paceSec: resolvedPace,
        }),
      );
    }
  }

  if (lines.length > 0) return finish();

  const zoneDistance = [...text.matchAll(
    /(\d+(?:[.,]\d+)?)\s*(?:k(?:m)?|kilometers?)\s*(?:at\s*)?(?:@?\s*)?(?:(\d{1,2}:\d{2})\s*(?:\/\s*k(?:m)?|pace)?\s*)?(?:in\s*)?(?:zone\s*|z)\s*([1-5])/gi,
  )];
  if (zoneDistance.length >= 1) {
    zoneDistance.forEach((m) => {
      const km = m[1].replace(',', '.');
      const zNum = parseInt(m[3], 10);
      const zId = `z${zNum}` as HrZoneId;
      const paceSec =
        m[2] ? parsePaceToSec(m[2]) : resolvePaceForZone(zId, personalPaces);
      lines.push(
        formatCoachPhaseLine(
          'Steady run',
          `Run ${km} km`,
          zones,
          { zNum, zone: zoneForNumber(zNum, zones), paceSec },
        ),
      );
    });
    return finish();
  }

  const zoneTimeAll = [...text.matchAll(ZONE_TIME_CHUNK)];
  if (zoneTimeAll.length >= 1) {
    zoneTimeAll.forEach((m) => {
      const mins = parseFloat(m[1]);
      const zNum = parseInt(m[2], 10);
      const zId = `z${zNum}` as HrZoneId;
      lines.push(
        formatCoachPhaseLine(
          'Steady block',
          `${mins} minute${mins !== 1 ? 's' : ''}`,
          zones,
          {
            zNum,
            zone: zoneForNumber(zNum, zones),
            paceSec: resolvePaceForZone(zId, personalPaces),
          },
        ),
      );
    });
    return finish();
  }

  const wholeInterval = parseIntervalSegment(text, personalPaces);
  if (wholeInterval && INTERVAL_SET_REGEX.test(text)) {
    return maybeWrapWithWarmCool(
      [formatIntervalSegment(wholeInterval, zones, personalPaces)],
      segments,
      zones,
    ).join('\n');
  }

  const goalRun = parseRaceGoal(text);
  if (goalRun) {
    const paceSec = goalRun.paceSecondsPerKm;
    const zNum = paceSec
      ? parseInt(resolveZoneForPace(paceSec, personalPaces).replace('z', ''), 10)
      : 3;
    return maybeWrapWithWarmCool(
      [
        formatCoachPhaseLine(
          'Target run',
          `${goalRun.label} in ${formatRaceTimeForCoach(goalRun.durationSeconds)}`,
          zones,
          { zNum, zone: zoneForNumber(zNum, zones), paceSec },
        ),
      ],
      segments,
      zones,
    ).join('\n');
  }

  return text;
}

/** Split comma-separated zone/distance segments onto separate lines before parsing. */
function expandCommaSeparatedZoneSegments(text: string): string {
  if (!/(?:zone\s*[1-5]|z[1-5])/i.test(text)) return text;
  if (text.includes('\n')) return text;
  const parts = text.split(/\s*,\s*(?=\d)/).map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) return text;
  return parts.join('\n');
}

/** Break single-line Arnold plans into parseable lines (warm-up, stretches, intervals, cool-down). */
function expandStructuredPlanLines(text: string): string {
  if (text.includes('\n')) return text;
  let out = text.trim();
  out = out.replace(/\s+(?=\d+\s*min(?:ute)?s?\s+(?:dynamic|static)\s+stretch)/gi, '\n');
  out = out.replace(/\s+(?=(\d+)\s*[x×]\s*\d+)/gi, '\n');
  out = out.replace(/\s+(?=\d+\s*min(?:ute)?s?\s+cool-?down)/gi, '\n');
  out = out.replace(/\s+(?=\d+\s*min(?:ute)?s?\s+warm-?up)/gi, '\n');
  out = out.replace(/\s+(?=\d+\s*min(?:ute)?s?\s+(?:easy|tempo|long|steady)\b)/gi, '\n');
  out = out.replace(/\s+(?=\d+\s*min(?:ute)?s?\s+(?:comfortably|near|hard)\b)/gi, '\n');
  // Split embedded main blocks: "10 min warm-up … 4:30 pace 6km … 10 min cool-down"
  out = out.replace(/\s+(?=\d{1,2}:\d{2}\s*pace\s+\d)/gi, '\n');
  out = out.replace(/\s+(?=main\s+set\s*[—–\-:])/gi, '\n');
  return out;
}

/** Strip leading list markers like "1. " or "2) " — never strip decimals like "1.5 km". */
const stripLinePrefix = (line: string): string =>
  line.replace(/^\d+[.)]\s+/, '').trim();

/** Strip structured phase labels before parsing rep/zone tokens. */
const stripPhaseLabelPrefix = (line: string): string =>
  line
    .replace(/^(?:warm-up|cool-down|main set|steady(?: run| block)?|target run)\s*[—–\-:]\s*/i, '')
    .trim();

/** Read warm-up / cool-down from "Warm-up — …" before the label is stripped. */
const extractPhaseLabelKind = (line: string): RunPhaseKind | null => {
  if (/^warm-up\s*[—–-]/i.test(line)) return 'warmup';
  if (/^cool-down\s*[—–-]/i.test(line)) return 'cooldown';
  return null;
};

/** Multi-phase plans must not collapse into a single distance+pace shortcut. */
function isStructuredMultiPartPlan(text: string): boolean {
  const trimmed = normalizeCoachPlanText(text).trim();
  if (!trimmed) return false;
  const lines = trimmed.split(/[\n\r]+/).map((l) => l.trim()).filter(Boolean);
  if (lines.length >= 2) return true;
  return isIntervalStylePlan(trimmed);
}

/**
 * Split a plan into one phase per "line" (sentence / list item) and try to
 * extract duration, distance, target zone, pace, and rep count from each.
 */
function parseRunPlanInner(plan: unknown): ParsedRunStructure {
  const text = expandStructuredPlanLines(
    expandCommaSeparatedZoneSegments(normalizeCoachPlanText(plan).trim()),
  );
  if (!text) {
    return { phases: [], totalPlannedSeconds: 0, hasStructure: false };
  }

  const paceTargetPhase = !isStructuredMultiPartPlan(text) ? parseDistanceWithPaceTarget(text) : null;
  if (paceTargetPhase) {
    return {
      phases: [{ ...paceTargetPhase, index: 0 }],
      totalPlannedSeconds: 0,
      hasStructure: true,
    };
  }

  const goalRun = parseRaceGoal(text);
  if (goalRun && isSingleRaceGoalPlan(text)) {
    const phase = raceGoalToPhase(goalRun, 0);
    return {
      phases: [phase],
      totalPlannedSeconds: goalRun.durationSeconds,
      hasStructure: true,
    };
  }

  const rawLines = text
    .split(/[\n\r]+/)
    .flatMap((block) => {
      const segments = splitPlanSegments(block);
      return segments.flatMap((segment) =>
        segment.split(/(?:(?<=[a-z0-9])\.\s+(?=[A-Z]))|;\s*|\u2022\s*|\s+-\s+/g),
      );
    })
    .map((line) => stripLinePrefix(line.trim()))
    .filter(Boolean);

  const phases: RunPhase[] = [];
  let cursor = 0;

  for (const rawLine of rawLines) {
    if (isMetadataLine(rawLine) || isPacingNoteLine(rawLine)) continue;

    const labelKind = extractPhaseLabelKind(rawLine);
    const line = stripPhaseLabelPrefix(rawLine);

    if (/\b(?:dynamic|static)\s+stretch/i.test(line)) {
      const stretchSec = parseDurationToSeconds(line);
      if (stretchSec) {
        const isDynamic = /dynamic/i.test(line);
        if (phases.length > 0) {
          const last = phases[phases.length - 1];
          if (isDynamic && last.kind === 'warmup') {
            last.durationSeconds = (last.durationSeconds ?? 0) + stretchSec;
            const cue = cueFor('warmup', {
              zone: last.targetZone,
              durationSec: last.durationSeconds,
            });
            last.endCue = cue.end;
            continue;
          }
          if (!isDynamic && last.kind === 'cooldown') {
            last.durationSeconds = (last.durationSeconds ?? 0) + stretchSec;
            continue;
          }
        }
        const stretchCue = isDynamic
          ? {
              start: `Dynamic stretching for ${formatDurationSpeech(stretchSec)}. Pause running and loosen up.`,
              end: 'Stretches done. Get ready for the main effort.',
            }
          : {
              start: `Static stretching for ${formatDurationSpeech(stretchSec)}. Slow to a stop and stretch.`,
              end: 'Stretching complete. Great session.',
            };
        phases.push({
          kind: isDynamic ? 'warmup' : 'cooldown',
          index: cursor,
          label: isDynamic ? 'Dynamic stretches' : 'Static stretching',
          durationSeconds: stretchSec,
          startCue: stretchCue.start,
          endCue: stretchCue.end,
        });
        cursor = phases.length;
        continue;
      }
    }

    const expanded = expandIntervalSet(line, cursor);
    if (expanded && expanded.length > 0) {
      phases.push(...expanded);
      cursor = phases.length;
      continue;
    }

    const kind = classifyLine(line, labelKind);
    const durationSeconds = parseDurationToSeconds(line);
    let distanceKm = parseDistanceToKm(line);
    if (isRacePaceReferenceOnly(line)) distanceKm = undefined;
    const zone = parseZone(line);
    let pace = parsePaceToSec(line);
    const hasActionableTarget =
      !!durationSeconds ||
      !!distanceKm ||
      !!zone ||
      !!pace ||
      !!labelKind;
    if (!hasActionableTarget) continue;
    if (kind === 'free' && !durationSeconds && !distanceKm && !zone) continue;
    // Bare "recovery" labels without duration are pacing notes, not phases.
    if (kind === 'recovery' && !durationSeconds && !distanceKm) continue;

    const lineGoal = parseRaceGoal(line);
    if (lineGoal) {
      phases.push(raceGoalToPhase(lineGoal, cursor));
      cursor = phases.length;
      continue;
    }

    if (!pace && distanceKm && durationSeconds) {
      pace = paceFromDistanceAndDuration(distanceKm, durationSeconds);
    }

    const cue = cueFor(kind, { zone, pace, durationSec: durationSeconds, distanceKm });
    phases.push({
      kind,
      index: cursor,
      label: labelFromLine(line, kind, cursor),
      durationSeconds,
      distanceKm,
      targetZone: zone,
      paceSecondsPerKm: pace,
      startCue: cue.start,
      endCue: cue.end,
    });
    cursor = phases.length;
  }

  if (phases.length === 0) {
    phases.push({
      kind: 'free',
      index: 0,
      label: 'Run',
      startCue: 'Free run. Keep a steady rhythm and listen to your body.',
    });
  }

  const phasesWithRecovery = phases;

  const totalPlannedSeconds = phasesWithRecovery.reduce(
    (acc, p) => acc + (p.durationSeconds || p.targetTimeSeconds || 0),
    0,
  );
  const hasStructure = phasesWithRecovery.some(
    (p) =>
      p.durationSeconds ||
      p.distanceKm ||
      p.targetZone ||
      p.paceSecondsPerKm ||
      (p.kind !== 'free' && (p.durationSeconds || p.distanceKm || p.targetZone)),
  );
  return { phases: phasesWithRecovery, totalPlannedSeconds, hasStructure };
}

export function parseRunPlan(plan: unknown): ParsedRunStructure {
  try {
    return parseRunPlanInner(plan);
  } catch (err) {
    console.warn('[parseRunPlan] failed:', err instanceof Error ? err.message : String(err));
    const text = normalizeCoachPlanText(plan).trim();
    return {
      phases: [
        {
          kind: 'free',
          index: 0,
          label: 'Run',
          startCue: text
            ? 'Follow your plan. I will guide effort and pacing throughout.'
            : 'Free run. Keep a steady rhythm and listen to your body.',
        },
      ],
      totalPlannedSeconds: 0,
      hasStructure: false,
    };
  }
}

/**
 * Decide which phase the runner is currently in based on elapsed time and
 * distance. Returns the active phase + remaining seconds (when knowable).
 */
/** Default dwell per phase when the plan has structure but a line has no duration/distance. */
const DEFAULT_PHASE_DWELL_SEC = 180;

const effectivePhaseSeconds = (
  phase: RunPhase,
  structure: ParsedRunStructure,
): number => {
  const explicit = phase.durationSeconds ?? 0;
  if (explicit > 0) return explicit;
  if ((phase.distanceKm ?? 0) > 0) return 0;

  if (!structure.hasStructure || structure.phases.length <= 1) return 0;

  const unspecified = structure.phases.filter(
    (p) => !(p.durationSeconds && p.durationSeconds > 0) && !(p.distanceKm && p.distanceKm > 0),
  ).length;
  if (unspecified <= 0) return DEFAULT_PHASE_DWELL_SEC;

  if (structure.totalPlannedSeconds > 0) {
    return Math.min(600, Math.max(60, Math.round(structure.totalPlannedSeconds / unspecified)));
  }
  if (unspecified <= 0) return 0;
  return DEFAULT_PHASE_DWELL_SEC;
};

export type ActivePhaseState = {
  phase: RunPhase;
  remainingSeconds: number | null;
  remainingDistanceKm: number | null;
  distanceIntoPhaseKm: number | null;
  progress: number;
  /** Total GPS distance (m) when this phase began. */
  phaseStartDistanceMeters?: number;
  /** Total GPS distance (m) when this distance phase completes. */
  phaseCompleteAtDistanceMeters?: number;
};

export type GetActivePhaseOptions = {
  /**
   * Watch distance (km) when each distance-bounded phase started.
   * Required after warm-up / recovery so interval meters are measured from the
   * phase transition, not re-anchored to current GPS every tick.
   */
  phaseStartDistanceKmByIndex?: ReadonlyMap<number, number>;
  /**
   * Elapsed run seconds when each distance-bounded phase first completed.
   * Without this, time-based recovery after an interval never ends because
   * accSec would be re-set to the current elapsed time on every GPS tick.
   */
  phaseEndElapsedSecondsByIndex?: Map<number, number>;
};

export function getActivePhase(
  structure: ParsedRunStructure,
  elapsedSeconds: number,
  distanceKm: number,
  options?: GetActivePhaseOptions,
): ActivePhaseState | null {
  if (structure.phases.length === 0) return null;

  let accSec = 0;
  /**
   * Planned-distance cursor for back-to-back distance blocks.
   * After a time block, the next distance block uses phaseStartDistanceKmByIndex.
   */
  let distBaselineKm = 0;
  let snapBaselineAfterTimePhase = false;

  for (const phase of structure.phases) {
    const phaseSec = effectivePhaseSeconds(phase, structure);
    const phaseKm = phase.distanceKm ?? 0;

    // Time-bounded phase (explicit or estimated dwell)
    if (phaseSec > 0) {
      const end = accSec + phaseSec;
      if (elapsedSeconds < end) {
        const into = elapsedSeconds - accSec;
        return {
          phase,
          remainingSeconds: Math.max(0, Math.round(end - elapsedSeconds)),
          remainingDistanceKm: null,
          distanceIntoPhaseKm: null,
          progress: phaseSec > 0 ? into / phaseSec : 0,
          phaseStartDistanceMeters: Math.round(distBaselineKm * 1000),
        };
      }
      const endElapsedMap = options?.phaseEndElapsedSecondsByIndex;
      if (endElapsedMap && !endElapsedMap.has(phase.index)) {
        endElapsedMap.set(phase.index, end);
      }
      accSec = end;
      snapBaselineAfterTimePhase = true;
      continue;
    }

    // Distance-bounded phase — interval meters stack on distance already covered in prior blocks.
    if (phaseKm > 0) {
      let phaseBaselineKm = distBaselineKm;
      if (snapBaselineAfterTimePhase) {
        const anchored = options?.phaseStartDistanceKmByIndex?.get(phase.index);
        if (anchored != null && Number.isFinite(anchored)) {
          phaseBaselineKm = anchored;
          snapBaselineAfterTimePhase = false;
        } else {
          // Anchor pending — caller will record GPS at phase entry and re-evaluate.
          return {
            phase,
            remainingSeconds: null,
            remainingDistanceKm: phaseKm,
            distanceIntoPhaseKm: 0,
            progress: 0,
            phaseStartDistanceMeters: Math.round(distanceKm * 1000),
            phaseCompleteAtDistanceMeters: Math.round((distanceKm + phaseKm) * 1000),
          };
        }
      }
      const intoKm = Math.max(0, distanceKm - phaseBaselineKm);
      const completeAtKm = phaseBaselineKm + phaseKm;
      if (intoKm < phaseKm) {
        return {
          phase,
          remainingSeconds: null,
          remainingDistanceKm: Math.max(0, phaseKm - intoKm),
          distanceIntoPhaseKm: intoKm,
          progress: phaseKm > 0 ? intoKm / phaseKm : 0,
          phaseStartDistanceMeters: Math.round(phaseBaselineKm * 1000),
          phaseCompleteAtDistanceMeters: Math.round(completeAtKm * 1000),
        };
      }
      distBaselineKm = phaseBaselineKm + phaseKm;
      const endElapsedMap = options?.phaseEndElapsedSecondsByIndex;
      if (endElapsedMap && !endElapsedMap.has(phase.index)) {
        endElapsedMap.set(phase.index, elapsedSeconds);
      }
      const phaseEndElapsed = endElapsedMap?.get(phase.index) ?? elapsedSeconds;
      accSec = Math.max(accSec, phaseEndElapsed);
      continue;
    }

    // Single free-form phase with no bounds
    if (structure.phases.length === 1) {
      return {
        phase,
        remainingSeconds: null,
        remainingDistanceKm: phaseKm > 0 ? phaseKm : null,
        distanceIntoPhaseKm: distanceKm,
        progress: 0,
      };
    }
  }

  const last = structure.phases[structure.phases.length - 1];
  return {
    phase: last,
    remainingSeconds: 0,
    remainingDistanceKm: 0,
    distanceIntoPhaseKm: last.distanceKm ?? null,
    progress: 1,
  };
}

/**
 * Resolve active phase and record GPS distance anchors when entering each
 * distance-bounded block (interval, etc.).
 */
export function getActivePhaseWithDistanceAnchors(
  structure: ParsedRunStructure,
  elapsedSeconds: number,
  distanceKm: number,
  phaseStartDistanceKmByIndex: Map<number, number>,
  phaseEndElapsedSecondsByIndex: Map<number, number>,
  lastPhaseIndexRef: { current: number },
  phaseStartElapsedSecondsByIndex?: Map<number, number>,
): ActivePhaseState | null {
  const phaseOptions = {
    phaseStartDistanceKmByIndex,
    phaseEndElapsedSecondsByIndex,
  };

  let active = getActivePhase(structure, elapsedSeconds, distanceKm, phaseOptions);
  if (!active) return null;

  const idx = active.phase.index;
  const isDistancePhase = (active.phase.distanceKm ?? 0) > 0;
  const phaseChanged = lastPhaseIndexRef.current !== idx;

  if (isDistancePhase && (phaseChanged || !phaseStartDistanceKmByIndex.has(idx))) {
    if (!phaseStartDistanceKmByIndex.has(idx)) {
      phaseStartDistanceKmByIndex.set(idx, distanceKm);
    }
    if (phaseStartElapsedSecondsByIndex && !phaseStartElapsedSecondsByIndex.has(idx)) {
      phaseStartElapsedSecondsByIndex.set(idx, elapsedSeconds);
    }
    if (phaseChanged) lastPhaseIndexRef.current = idx;
    active = getActivePhase(structure, elapsedSeconds, distanceKm, phaseOptions);
  } else if (phaseChanged) {
    if (phaseStartElapsedSecondsByIndex && !phaseStartElapsedSecondsByIndex.has(idx)) {
      phaseStartElapsedSecondsByIndex.set(idx, elapsedSeconds);
    }
    lastPhaseIndexRef.current = idx;
  }

  return active;
}

/**
 * GPS-aware mid-phase cue — last interval, 200 m to go, etc.
 * Returns null when no transition cue applies.
 */
export function buildPhaseTransitionCue(active: ActivePhaseState): string | null {
  const { phase, remainingDistanceKm, remainingSeconds, progress } = active;
  const remM =
    remainingDistanceKm != null && remainingDistanceKm > 0
      ? Math.round(remainingDistanceKm * 1000)
      : null;

  if (
    (remainingDistanceKm ?? 1) <= 0.05 &&
    progress >= 0.99 &&
    (phase.distanceKm ?? 0) > 0
  ) {
    return 'Workout complete — you hit your distance. Great effort.';
  }

  if (phase.kind === 'interval' && phase.repInSet) {
    const { rep, totalReps } = phase.repInSet;
    const isLast = rep === totalReps;

    if (remM != null && remM <= 200 && remM > 80) {
      if (isLast) {
        return `Two hundred meters left on your final interval — finish strong.`;
      }
      return `Two hundred meters left in interval ${rep} of ${totalReps} — hold your pace.`;
    }
    if (remM != null && remM <= 80 && remM > 30) {
      if (isLast) {
        return `Final push — less than ${remM} meters on the last interval.`;
      }
      return `Almost there — ${remM} meters left in this interval.`;
    }
    if (progress >= 0.5 && progress < 0.55 && remM != null && remM > 250) {
      return `Halfway through interval ${rep} of ${totalReps}. Stay controlled.`;
    }
    if (isLast && progress < 0.08 && (phase.distanceKm ?? 0) > 0) {
      return `Last interval — this is the one. Lock into your target pace.`;
    }
  }

  if (phase.targetTimeSeconds && phase.distanceKm && remM != null) {
    if (progress >= 0.48 && progress < 0.54) {
      return `Halfway through your ${phase.label.replace(/ race$/i, '')} — stay patient and hold your rhythm.`;
    }
    if (remM <= 1000 && remM > 800) {
      return `One kilometer to go — this is where you commit. Stay strong.`;
    }
    if (remM <= 500 && remM > 350) {
      return `Five hundred meters left — dig in and finish this race.`;
    }
    if (remM <= 200 && remM > 80) {
      return `Two hundred meters — give everything you've got left.`;
    }
  }

  if (remM != null && remM <= 200 && remM > 50 && phase.kind !== 'recovery') {
    const block =
      phase.kind === 'tempo'
        ? 'tempo block'
        : phase.kind === 'warmup'
          ? 'warm-up'
          : phase.kind === 'cooldown'
            ? 'cool-down'
            : 'this block';
    return `${remM} meters left in your ${block}.`;
  }

  if (
    remainingSeconds != null &&
    remainingSeconds <= 30 &&
    remainingSeconds > 10 &&
    phase.kind !== 'recovery'
  ) {
    return `Thirty seconds left in ${phase.label.toLowerCase()}.`;
  }

  return null;
}

/**
 * Stable dedup key for a transition cue — one announcement per milestone per phase.
 */
export function getPhaseTransitionCueKey(active: ActivePhaseState): string | null {
  if (!buildPhaseTransitionCue(active)) return null;

  const { phase, remainingDistanceKm, remainingSeconds, progress } = active;
  const remM =
    remainingDistanceKm != null && remainingDistanceKm > 0
      ? Math.round(remainingDistanceKm * 1000)
      : null;

  if (
    (remainingDistanceKm ?? 1) <= 0.05 &&
    progress >= 0.99 &&
    (phase.distanceKm ?? 0) > 0
  ) {
    return `${phase.index}:workout-complete`;
  }

  if (phase.kind === 'interval' && phase.repInSet) {
    if (remM != null && remM <= 200 && remM > 80) return `${phase.index}:200m`;
    if (remM != null && remM <= 80 && remM > 30) return `${phase.index}:final-push`;
    if (progress >= 0.5 && progress < 0.55 && remM != null && remM > 250) return `${phase.index}:half`;
    if (
      phase.repInSet.rep === phase.repInSet.totalReps &&
      progress < 0.08 &&
      (phase.distanceKm ?? 0) > 0
    ) {
      return `${phase.index}:last-start`;
    }
  }

  if (phase.targetTimeSeconds && phase.distanceKm && remM != null) {
    if (progress >= 0.48 && progress < 0.54) return `${phase.index}:race-half`;
    if (remM <= 1000 && remM > 800) return `${phase.index}:race-1k`;
    if (remM <= 500 && remM > 350) return `${phase.index}:race-500m`;
    if (remM <= 200 && remM > 80) return `${phase.index}:race-200m`;
  }

  if (remM != null && remM <= 200 && remM > 50 && phase.kind !== 'recovery') {
    return `${phase.index}:block-200m`;
  }

  if (
    remainingSeconds != null &&
    remainingSeconds <= 30 &&
    remainingSeconds > 10 &&
    phase.kind !== 'recovery'
  ) {
    return `${phase.index}:30s-left`;
  }

  return null;
}

/** Natural-language list join: ["a", "b", "c"] → "a, b, and c". */
const joinList = (parts: string[]): string => {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
};

const speakDuration = (sec?: number): string => {
  if (!sec || sec <= 0) return '';
  if (sec < 60) return `${sec} second${sec !== 1 ? 's' : ''}`;
  const m = Math.round(sec / 60);
  return `${m} minute${m !== 1 ? 's' : ''}`;
};

const speakDistance = (km?: number): string => {
  if (!km || km <= 0) return '';
  if (km < 1) return `${Math.round(km * 1000)} meter${Math.round(km * 1000) !== 1 ? 's' : ''}`;
  // Trim trailing .0 ("5 kilometers" not "5.0 kilometers")
  const v = km.toFixed(km < 10 ? 1 : 0).replace(/\.0$/, '');
  return `${v} kilometer${v === '1' ? '' : 's'}`;
};

const speakZone = (zone?: HrZoneId): string => {
  if (!zone) return '';
  const num = zone.replace('z', '');
  return `zone ${num}`;
};

const speakPace = (sec?: number): string => {
  if (!sec || sec <= 0) return '';
  const totalSec = Math.max(0, Math.round(sec));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m <= 0) return `${s} seconds per kilometer pace`;
  if (s === 0) return `${m} minute pace per kilometer`;
  return `${m} ${s.toString().padStart(2, '0')} pace per kilometer`;
};

/** Returns the "at zone X" / "at Y pace" / "at zone X, Y pace" suffix. */
const speakIntensity = (phase: RunPhase): string => {
  const zone = speakZone(phase.targetZone);
  const pace = speakPace(phase.paceSecondsPerKm);
  if (zone && pace) return ` at ${zone}, ${pace}`;
  if (zone) return ` at ${zone}`;
  if (pace) return ` at ${pace}`;
  return '';
};

const describeSinglePhase = (phase: RunPhase): string => {
  const dur = speakDuration(phase.durationSeconds ?? phase.targetTimeSeconds);
  const dist = speakDistance(phase.distanceKm);
  const intensity = speakIntensity(phase);
  const block = dur || dist;

  switch (phase.kind) {
    case 'warmup':
      return block ? `${block} warm-up${intensity}` : `a warm-up${intensity}`;
    case 'cooldown':
      return block ? `${block} cool-down` : `a cool-down`;
    case 'recovery':
      return block ? `${block} recovery` : 'a short recovery';
    case 'tempo':
      return block ? `${block} tempo block${intensity}` : `a tempo block${intensity}`;
    case 'easy':
      return block ? `${block} easy run${intensity}` : `an easy run${intensity}`;
    case 'long':
      return block ? `${block} long run${intensity}` : `a long run${intensity}`;
    case 'interval':
      return block ? `${block} interval${intensity}` : `an interval${intensity}`;
    default:
      return block ? `${block} of running${intensity}` : '';
  }
};

/**
 * Build a short, speakable summary of the plan structure for the coach intro.
 * Groups consecutive intervals with the same rep count into "6 by 800 meter
 * intervals at zone 4 with 90 seconds recovery".
 */
export function buildPlanSummarySpeech(structure: ParsedRunStructure): string {
  if (!structure.hasStructure || structure.phases.length === 0) return '';

  const hasIntervals = structure.phases.some((p) => p.kind === 'interval');
  if (!hasIntervals) {
    const parts: string[] = [];
    for (const phase of structure.phases) {
      if (phase.kind === 'free') continue;
      if (phase.kind === 'recovery' && !(phase.durationSeconds && phase.durationSeconds > 0)) continue;
      const d = describeSinglePhase(phase);
      if (!d) continue;
      parts.push(d);
    }
    return joinList(parts);
  }

  const parts: string[] = [];
  const warmup = structure.phases.find((p) => p.kind === 'warmup');
  const cooldown = [...structure.phases].reverse().find((p) => p.kind === 'cooldown');
  if (warmup) {
    const d = describeSinglePhase(warmup);
    if (d) parts.push(d);
  }

  let i = 0;
  while (i < structure.phases.length) {
    const phase = structure.phases[i];

    if (phase.kind === 'warmup' || phase.kind === 'cooldown') {
      i++;
      continue;
    }

    // Standalone recovery (between intervals) — skip; folded into interval set line.
    if (phase.kind === 'recovery' && i > 0 && i < structure.phases.length - 1) {
      i++;
      continue;
    }

    // Group consecutive intervals with same totalReps.
    if (phase.kind === 'interval' && phase.repInSet) {
      const totalReps = phase.repInSet.totalReps;
      const distOrDur = speakDistance(phase.distanceKm) || speakDuration(phase.durationSeconds);
      const intensity = speakIntensity(phase);
      const intervalLabel = distOrDur
        ? `${totalReps} by ${distOrDur} interval${totalReps !== 1 ? 's' : ''}${intensity}`
        : `${totalReps} interval${totalReps !== 1 ? 's' : ''}${intensity}`;

      // Look for recovery between intervals in the same set to mention it.
      let recoveryDesc = '';
      let j = i + 1;
      while (j < structure.phases.length) {
        const p = structure.phases[j];
        if (p.kind === 'interval' && p.repInSet?.totalReps === totalReps) { j++; continue; }
        if (p.kind === 'recovery') {
          if (!recoveryDesc) {
            const r = speakDuration(p.durationSeconds);
            if (r) recoveryDesc = ` with ${r} easy recovery between`;
          }
          j++;
          continue;
        }
        break;
      }

      parts.push(`${intervalLabel}${recoveryDesc}`);
      i = j;
      continue;
    }

    const desc = describeSinglePhase(phase);
    if (desc) parts.push(desc);
    i++;
  }

  if (cooldown && cooldown !== warmup) {
    const d = describeSinglePhase(cooldown);
    if (d) parts.push(d);
  }

  return joinList(parts);
}

/**
 * Coach intro: one pass over structural phases only (no pacing-guide bullets).
 */
export function buildPlanIntroSpeech(planText: string, structure: ParsedRunStructure): string {
  const summary = buildPlanSummarySpeech(structure);
  if (summary) return summary;

  const lines = (planText || '')
    .split(/[\n\r]+/)
    .map((l) => l.replace(/^#+\s*/, '').replace(/^[-•*]\s*/, '').trim())
    .filter((l) => l && !isMetadataLine(l) && !isPacingNoteLine(l));
  return joinList(lines.slice(0, 4).map((l) => l.replace(/\bZ(\d)\b/gi, 'zone $1').slice(0, 90)));
}
